import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  CalendarDays,
  Library,
  MapPin,
  MessageCircle,
  PencilLine,
  PanelsTopLeft,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  EmptyState,
  PanelActionButton,
  StatusPill,
  SummaryTile,
} from "@/components/platform/ContentPrimitives";
import { api, getApiErrorMessage } from "@/lib/api";
import AdminContentEdit from "@/components/platform/AdminContentEdit";
import { formatDateTime } from "@/lib/formatters";

const CONTENT_CONFIG = [
  {
    id: "initiatives",
    label: "Iniciativas",
    icon: MapPin,
    deleteType: "initiative",
    title: (item) => item.nombre,
    description: (item) => item.descripcion,
    meta: (item) => [item.municipio, item.comuna, item.barrio].filter(Boolean).join(" · "),
    image: (item) => item.image_url,
    editPath: (id) => `/registrar-iniciativa?edit=${id}`,
  },
  {
    id: "posts",
    label: "Mercado",
    icon: ShoppingBag,
    deleteType: "marketplace_post",
    title: (item) => item.titulo,
    description: (item) => item.descripcion,
    meta: (item) => [item.municipio, item.comuna, item.barrio].filter(Boolean).join(" · "),
    image: (item) => item.imagenes?.[0],
    editPath: (id) => `/crear-publicacion?edit=${id}`,
  },
  {
    id: "events",
    label: "Calendario",
    icon: CalendarDays,
    deleteType: "event",
    title: (item) => item.nombre,
    description: (item) => item.descripcion,
    meta: (item) => [item.municipio, item.comuna, formatDateTime(item.fecha_inicio)].filter(Boolean).join(" · "),
    image: (item) => item.imagenes?.[0],
    editPath: (id) => `/calendario?edit=${id}`,
  },
  {
    id: "forum_questions",
    label: "Foro",
    icon: BookOpen,
    deleteType: "forum_question",
    title: (item) => item.titulo,
    description: (item) => item.descripcion,
    meta: (item) => (item.etiquetas || []).join(" · "),
    image: () => null,
    editPath: (id) => `/conocimiento?tab=foro&editQuestion=${id}`,
  },
  {
    id: "forum_answers",
    label: "Respuestas",
    icon: MessageCircle,
    deleteType: "forum_answer",
    title: (item) => item.pregunta?.titulo || "Respuesta del foro",
    description: (item) => item.contenido,
    meta: (item) => `Autor: ${item.autor?.nombre || "Usuario"}`,
    inlineEdit: true,
  },
  {
    id: "library_documents",
    label: "Biblioteca",
    icon: Library,
    deleteType: "library_document",
    title: (item) => item.titulo,
    description: (item) => item.descripcion,
    meta: (item) => (item.etiquetas || []).join(" · "),
    image: (item) => item.imagenes_portada?.[0],
    editPath: (id) => `/conocimiento?tab=biblioteca&editDocument=${id}`,
  },
];

function TabButton({ active, count, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-3 rounded-full border px-4 py-2.5 text-sm font-semibold transition ${
        active
          ? "border-primary bg-primary text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)]"
          : "border-primary/10 bg-white text-primary hover:bg-[#F4F7EF]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      <span
        className={`inline-flex min-w-7 items-center justify-center rounded-full px-2 py-0.5 text-xs ${
          active ? "bg-white/15" : "bg-primary/10 text-primary"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function ContentCard({ config, item, busyKey, onDelete, onEdit, children }) {
  const Icon = config.icon;
  const image = config.image(item);

  return (
    <article
      id={`admin-${config.deleteType}-${item.id}`}
      className="overflow-hidden rounded-[30px] border border-primary/10 bg-white shadow-sm"
    >
      <div className="grid gap-0 lg:grid-cols-[260px_1fr]">
        <div className="min-h-[220px] bg-[#EEF4E7]">
          {image ? (
            <img src={image} alt={config.title(item)} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center bg-[linear-gradient(145deg,#E8F0DD,#F6F7F0)] text-primary/55">
              <Icon className="h-10 w-10" />
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex flex-wrap gap-2">
                <StatusPill tone={item.estado === "PUBLICADO" ? "positive" : "neutral"}>{item.estado || item.status || "Sin estado"}</StatusPill>
                <StatusPill tone="neutral">{config.label}</StatusPill>
              </div>
              <h3 className="mt-4 font-heading text-2xl font-semibold text-primary">{config.title(item)}</h3>
              <p className="mt-2 text-sm leading-7 text-textMain">{config.description(item) || "Sin descripción."}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <PanelActionButton tone="secondary" onClick={() => onEdit(item.id)}>
                <PencilLine className="h-4 w-4" />
                Editar
              </PanelActionButton>
              <PanelActionButton
                tone="danger"
                onClick={() => onDelete(item.id)}
                disabled={busyKey === `${config.id}-${item.id}-deleted`}
              >
                <Trash2 className="h-4 w-4" />
                Retirar
              </PanelActionButton>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-4 text-sm text-mutedForeground">
            <span>{config.meta(item) || "Sin metadatos relevantes"}</span>
            <span>{formatDateTime(item.created_at)}</span>
          </div>
          {children}
        </div>
      </div>
    </article>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("initiatives");
  const [viewMode, setViewMode] = useState("content");
  const [content, setContent] = useState({});
  const [users, setUsers] = useState([]);
  const [pendingInitiatives, setPendingInitiatives] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userDraft, setUserDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [editingContent, setEditingContent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === "users") {
        const response = await api.get("/admin/users");
        setUsers(response.data || []);
      } else if (viewMode === "review") {
        const response = await api.get("/admin/pending-initiatives");
        setPendingInitiatives(response.data || []);
      } else {
        const responses = await Promise.all(
          CONTENT_CONFIG.map((item) => api.get(`/admin/content/${item.deleteType}`)),
        );
        const nextContent = {};
        CONTENT_CONFIG.forEach((item, index) => {
          nextContent[item.id] = responses[index].data || [];
        });
        setContent(nextContent);
      }
    } catch (error) {
      toast.error("No fue posible cargar la gestión administrativa.");
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const requestedType = searchParams.get("type");
    const requestedId = searchParams.get("id");
    const config = CONTENT_CONFIG.find((item) => item.deleteType === requestedType);
    if (config) {
      setViewMode("content");
      setActiveTab(config.id);
      window.setTimeout(() => {
        document
          .getElementById(`admin-${config.deleteType}-${requestedId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 700);
    }
  }, [searchParams, content]);

  const contentTotals = useMemo(() => {
    const total = CONTENT_CONFIG.reduce((sum, item) => sum + (content[item.id]?.length || 0), 0);
    return {
      total,
      territory: (content.initiatives?.length || 0) + (content.events?.length || 0),
      knowledge:
        (content.forum_questions?.length || 0) +
        (content.forum_answers?.length || 0) +
        (content.library_documents?.length || 0),
    };
  }, [content]);

  const handleDelete = async (config, itemId) => {
    const currentKey = `${config.id}-${itemId}-deleted`;
    setBusyKey(currentKey);
    try {
      await api.delete(`/admin/content/${config.deleteType}/${itemId}`);
      toast.success("Contenido retirado.");
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No fue posible retirar el contenido."));
    } finally {
      setBusyKey("");
    }
  };

  const handleUserStatus = async (userId, status) => {
    const currentKey = `user-${userId}-${status}`;
    setBusyKey(currentKey);
    try {
      await api.post(`/admin/users/${userId}/approve`, { status });
      toast.success(status === "approved" ? "Usuario habilitado." : "Usuario desactivado.");
      await fetchData();
    } catch (error) {
      toast.error("No fue posible actualizar el usuario.");
    } finally {
      setBusyKey("");
    }
  };

  const startUserEdit = (item) => {
    setEditingUserId(item.id);
    setUserDraft({
      nombres: item.nombres || "",
      apellidos: item.apellidos || "",
      email: item.email || "",
      celular: item.celular || "No aplica",
      municipio: item.municipio || "No aplica",
      comuna: item.comuna || "No aplica",
      barrio: item.barrio || "No aplica",
    });
  };

  const saveUserEdit = async () => {
    const currentKey = `user-${editingUserId}-edit`;
    setBusyKey(currentKey);
    try {
      await api.put(`/admin/users/${editingUserId}`, userDraft);
      toast.success("Información del usuario actualizada.");
      setEditingUserId(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible actualizar el usuario.");
    } finally {
      setBusyKey("");
    }
  };

  const handleContentEdit = (config, item) => setEditingContent(`${config.id}-${item.id}`);

  const handleInitiativeReview = async (initiativeId, status) => {
    const currentKey = `initiative-${initiativeId}-${status}`;
    setBusyKey(currentKey);
    try {
      await api.post(`/admin/initiatives/${initiativeId}/approve`, { status });
      toast.success("Revisión registrada.");
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible registrar la revisión.");
    } finally {
      setBusyKey("");
    }
  };

  const activeConfig = CONTENT_CONFIG.find((item) => item.id === activeTab) || CONTENT_CONFIG[0];
  const activeItems = content[activeConfig.id] || [];

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="platform-hero-banner rounded-[34px] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">
              Administración de la plataforma
            </p>
            <h1 className="mt-3 flex items-center gap-3 font-heading text-4xl font-semibold text-primary sm:text-5xl">
              <ShieldCheck className="h-10 w-10" />
              Gestión administrativa
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-mutedForeground">
              Consulte el contenido publicado, edite información y retire registros cuando sea necesario. Las cuentas y
              publicaciones nuevas quedan activas de forma inmediata.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <PanelActionButton tone="secondary" onClick={fetchData}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </PanelActionButton>
            <PanelActionButton tone="primary" onClick={() => navigate("/admin/contenido")}>
              <PanelsTopLeft className="h-4 w-4" />
              Editar pantallas
            </PanelActionButton>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <SummaryTile
            icon={ShieldCheck}
            label="Contenido"
            value={contentTotals.total}
            description="Registros disponibles en los módulos."
          />
          <SummaryTile
            icon={MapPin}
            label="Territorio"
            value={contentTotals.territory}
            description="Iniciativas y encuentros publicados."
          />
          <SummaryTile
            icon={BookOpen}
            label="Conocimiento"
            value={contentTotals.knowledge}
            description="Preguntas y documentos publicados."
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {[
          ["content", "Contenido publicado"],
          ["users", "Usuarios"],
          ["review", "Revisión de iniciativas"],
        ].map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            onClick={() => setViewMode(mode)}
            className={`rounded-full border px-4 py-2.5 text-sm font-semibold ${
              viewMode === mode
                ? "border-primary bg-primary text-white"
                : "border-primary/10 bg-white text-primary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {viewMode === "content" ? (
        <div className="mt-4 flex flex-wrap gap-3">
          {CONTENT_CONFIG.map((item) => (
            <TabButton
              key={item.id}
              active={activeTab === item.id}
              count={content[item.id]?.length || 0}
              icon={item.icon}
              label={item.label}
              onClick={() => setActiveTab(item.id)}
            />
          ))}
        </div>
      ) : null}

      <div className="mt-6">
        {loading ? (
          <div className="platform-elevated-surface rounded-[30px] border px-6 py-16 text-center text-mutedForeground">
            Cargando gestión administrativa...
          </div>
        ) : viewMode === "review" ? (
          <div className="space-y-4">
            {pendingInitiatives.map((item) => (
              <article key={item.id} className="rounded-[26px] border border-primary/10 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <StatusPill tone="warning">Pendiente de revisión</StatusPill>
                    <h3 className="mt-3 font-heading text-2xl font-semibold text-primary">{item.nombre}</h3>
                    <p className="mt-2 text-sm leading-6 text-textMain">{item.descripcion}</p>
                    <p className="mt-2 text-xs text-mutedForeground">{[item.municipio, item.comuna, item.barrio].filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PanelActionButton onClick={() => handleInitiativeReview(item.id, "approved")}>Aprobar</PanelActionButton>
                    <PanelActionButton tone="secondary" onClick={() => handleInitiativeReview(item.id, "needs_changes")}>Solicitar cambios</PanelActionButton>
                    <PanelActionButton tone="danger" onClick={() => handleInitiativeReview(item.id, "rejected")}>Rechazar</PanelActionButton>
                  </div>
                </div>
              </article>
            ))}
            {!pendingInitiatives.length ? (
              <EmptyState icon={ShieldCheck} title="Sin iniciativas pendientes" description="La publicación inmediata está activa o no hay registros esperando revisión." />
            ) : null}
          </div>
        ) : viewMode === "users" ? (
          <div className="space-y-4">
            {users.map((item) => (
              <article
                key={item.id}
                className="flex flex-col gap-4 rounded-[26px] border border-primary/10 bg-white p-5 shadow-sm md:flex-row md:flex-wrap md:items-center md:justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-primary/10 p-3 text-primary">
                    <UserRound className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary">
                      {`${item.nombres || ""} ${item.apellidos || ""}`.trim()}
                    </p>
                    <p className="text-sm text-mutedForeground">
                      {item.email} · {item.status}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <PanelActionButton tone="secondary" onClick={() => startUserEdit(item)}>
                    <PencilLine className="h-4 w-4" />
                    Editar datos
                  </PanelActionButton>
                  <PanelActionButton
                    onClick={() => handleUserStatus(item.id, "approved")}
                    disabled={busyKey === `user-${item.id}-approved`}
                  >
                    Habilitar
                  </PanelActionButton>
                  <PanelActionButton
                    tone="danger"
                    onClick={() => handleUserStatus(item.id, "rejected")}
                    disabled={busyKey === `user-${item.id}-rejected`}
                  >
                    Desactivar
                  </PanelActionButton>
                </div>
                {editingUserId === item.id ? (
                  <div className="grid w-full basis-full gap-3 border-t border-primary/10 pt-4 md:grid-cols-2 lg:grid-cols-4">
                    {[
                      ["nombres", "Nombres"],
                      ["apellidos", "Apellidos"],
                      ["email", "Correo"],
                      ["celular", "Celular"],
                      ["municipio", "Municipio"],
                      ["comuna", "Comuna"],
                      ["barrio", "Barrio"],
                    ].map(([field, label]) => (
                      <label key={field} className="text-sm font-semibold text-primary">
                        {label}
                        <input
                          type={field === "email" ? "email" : "text"}
                          value={userDraft[field] || ""}
                          onChange={(event) => setUserDraft((current) => ({ ...current, [field]: event.target.value }))}
                          className="mt-2 h-11 w-full rounded-2xl border border-border bg-[#FCFCF9] px-3 font-normal text-textMain outline-none focus:border-primary/40"
                        />
                      </label>
                    ))}
                    <div className="flex items-end gap-2">
                      <PanelActionButton onClick={saveUserEdit} disabled={busyKey === `user-${item.id}-edit`}>
                        Guardar
                      </PanelActionButton>
                      <PanelActionButton tone="secondary" onClick={() => setEditingUserId(null)}>
                        <X className="h-4 w-4" />
                        Cancelar
                      </PanelActionButton>
                    </div>
                  </div>
                ) : null}
              </article>
            ))}
            {!users.length ? (
              <EmptyState icon={UserRound} title="No hay usuarios" description="No se encontraron cuentas registradas." />
            ) : null}
          </div>
        ) : !activeItems.length ? (
          <EmptyState
            icon={activeConfig.icon}
            title={`No hay ${activeConfig.label.toLowerCase()}`}
            description="No se encontraron registros publicados para este módulo."
          />
        ) : (
          <div className="space-y-5">
            {activeItems.map((item) => (
              <ContentCard
                key={`${activeConfig.id}-${item.id}`}
                config={activeConfig}
                item={item}
                busyKey={busyKey}
                onDelete={(itemId) => handleDelete(activeConfig, itemId)}
                onEdit={() => handleContentEdit(activeConfig, item)}
              >
                {editingContent === `${activeConfig.id}-${item.id}` ? (
                  <AdminContentEdit
                    entityType={activeConfig.deleteType}
                    item={item}
                    onCancel={() => setEditingContent(null)}
                    onSaved={async () => { setEditingContent(null); await fetchData(); }}
                    onFullEdit={activeConfig.editPath ? () => navigate(activeConfig.editPath(item.id)) : undefined}
                  />
                ) : null}
              </ContentCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
